import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Car, IdCard, Navigation, Clock, Loader2, UserPlus, CheckCircle2,
  MapPin, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../../lib/api";

/**
 * Painel "Ajudantes livres perto do cliente".
 * Sugere colaboradores DISPONÍVEIS do Banco de Colaboradores (pool) próximos
 * ao cliente e permite adicioná-los à equipe selecionada como ajudante temporário.
 * Dados reais via GET /talent-bank/match.
 */
export function NearbyHelpersPanel({ clientId, teamId, radiusMiles = 30 }) {
  const [loading, setLoading] = useState(false);
  const [helpers, setHelpers] = useState([]);
  const [added, setAdded] = useState({}); // id -> true
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/talent-bank/match", {
        params: { client_id: clientId, radius_miles: radiusMiles },
      });
      const free = (res.data?.matches || []).filter(
        (m) => m.source === "bank" && m.status === "disponivel",
      );
      setHelpers(free);
    } catch (err) {
      // sem localização do cliente ou nenhum match — trata como vazio
      setHelpers([]);
      setError(formatApiError(err?.response?.data?.detail) || "");
    } finally {
      setLoading(false);
    }
  }, [clientId, radiusMiles]);

  useEffect(() => { load(); }, [load]);

  const addToTeam = async (h) => {
    if (!teamId) {
      toast.error("Escolha uma equipe acima primeiro");
      return;
    }
    setBusyId(h.id);
    try {
      await api.post(`/talent-bank/${h.id}/link`, { team_id: teamId, tipo: "temporario" });
      toast.success(`${h.name} adicionado à equipe como ajudante`);
      setAdded((p) => ({ ...p, [h.id]: true }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!clientId) return null;

  return (
    <div
      data-testid="nearby-helpers-panel"
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50/60 p-3.5"
    >
      <button
        type="button"
        data-testid="nearby-helpers-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2"
      >
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shadow-sm shadow-emerald-600/30">
            <Sparkles size={15} />
          </span>
          <div className="text-left">
            <div className="text-sm font-bold text-emerald-900 leading-tight">
              Ajudantes livres na região
            </div>
            <div className="text-[11px] text-emerald-700/80">
              Do Banco de Colaboradores · até {radiusMiles} mi do cliente
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-none">
          {helpers.length > 0 && (
            <span
              data-testid="nearby-helpers-count"
              className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold"
            >
              {helpers.length} livre{helpers.length > 1 ? "s" : ""}
            </span>
          )}
          <ChevronDown
            size={18}
            className={`text-emerald-700 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && (
      <div className="mt-2.5">
      {loading ? (
        <div className="flex items-center gap-2 text-emerald-700 text-sm py-3">
          <Loader2 size={15} className="animate-spin" /> Buscando ajudantes próximos…
        </div>
      ) : helpers.length === 0 ? (
        <div
          data-testid="nearby-helpers-empty"
          className="text-sm text-emerald-800/80 bg-white/60 rounded-lg px-3 py-2.5 border border-emerald-100"
        >
          {error
            ? "O cliente ainda não tem localização no mapa — cadastre o endereço para ver ajudantes por perto."
            : "Nenhum ajudante livre por perto agora."}{" "}
          <Link to="/banco-colaboradores" className="font-semibold underline text-emerald-700">
            Ver banco completo
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="nearby-helpers-list">
          {helpers.map((h) => {
            const isAdded = !!added[h.id];
            return (
              <div
                key={h.id}
                data-testid={`nearby-helper-${h.id}`}
                className="flex items-center gap-3 bg-white rounded-lg border border-emerald-100 p-2.5"
              >
                {h.photo_path ? (
                  <img src={fileUrl(h.photo_path)} alt={h.name}
                    className="w-10 h-10 rounded-full object-cover flex-none" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold flex-none">
                    {(h.name || "?").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{h.name}</div>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                    {h.city && (
                      <span className="flex items-center gap-0.5"><MapPin size={11} /> {h.city}</span>
                    )}
                    {h.distance_miles != null && (
                      <span className="flex items-center gap-0.5 text-emerald-700 font-medium">
                        <Navigation size={11} /> {h.distance_miles} mi
                      </span>
                    )}
                    {h.eta_min != null && (
                      <span className="flex items-center gap-0.5"><Clock size={11} /> ~{h.eta_min} min</span>
                    )}
                    {h.has_car && <span className="flex items-center gap-0.5 text-indigo-600"><Car size={11} /> Carro</span>}
                    {h.has_license && <span className="flex items-center gap-0.5"><IdCard size={11} /> Driver</span>}
                  </div>
                </div>
                {isAdded ? (
                  <span
                    data-testid={`nearby-helper-added-${h.id}`}
                    className="flex items-center gap-1 text-emerald-700 text-xs font-bold flex-none px-2"
                  >
                    <CheckCircle2 size={15} /> Na equipe
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`nearby-helper-add-${h.id}`}
                    onClick={() => addToTeam(h)}
                    disabled={busyId === h.id}
                    title={teamId ? "Adicionar como ajudante temporário" : "Escolha uma equipe acima primeiro"}
                    className="flex-none inline-flex items-center gap-1.5 px-3 min-h-[36px] rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
                  >
                    {busyId === h.id
                      ? <Loader2 size={14} className="animate-spin" />
                      : <UserPlus size={14} />}
                    Adicionar
                  </button>
                )}
              </div>
            );
          })}
          <div className="text-[11px] text-emerald-700/70 pt-0.5">
            Ajudante temporário: fica na equipe até você "Marcar como finalizado" no Banco de Colaboradores.
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}
